import React, { useState, useRef, useEffect } from 'react'
import DatePicker from 'react-flatpickr'
import cx from 'classnames'
import subMonths from 'date-fns/sub_months'
import subYears from 'date-fns/sub_years'
import startOfMonth from 'date-fns/start_of_month'
import endOfMonth from 'date-fns/end_of_month'
import startOfYear from 'date-fns/start_of_year'
import endOfYear from 'date-fns/end_of_year'
import isWithinRange from 'date-fns/is_within_range'
import parse from 'date-fns/parse'
import isValid from 'date-fns/is_valid'
import { observable } from 'mobx'
import { observer } from 'mobx-react-lite'
import { css } from 'styled-components'
import { Trans } from '@lingui/react'
import { Slovak } from 'flatpickr/dist/l10n/sk'
import {
  startOfDay,
  endOfDay,
  addMonths,
  addYears,
  addHours,
  addMinutes,
  addSeconds,
  addMilliseconds,
} from 'date-fns'
import { DropdownItem } from '../../../ui-kit'
import { ButtonDropdown } from '../../../ui-kit/button/ButtonDropdown'
import { i18n } from '../../../utils/intl'
import { formatDate } from '../../../utils/format'
import { useDidUpdate } from '../../../utils/hooks/useDidUpdate'

export enum DateIntervals {
  thisMonth = 'thisMonth',
  previousMonth = 'previousMonth',
  thisYear = 'thisYear',
  previousYear = 'previousYear',
  nextMonth = 'nextMonth',
  nextYear = 'nextYear',
  custom = 'custom',
  all = 'all',
}

export const DateIntervalsLabelMap: Record<DateIntervals, string> = {
  thisMonth: i18n.t`Tento mesiac`,
  previousMonth: i18n.t`Minulý mesiac`,
  thisYear: i18n.t`Tento rok`,
  previousYear: i18n.t`Minulý rok`,
  nextMonth: i18n.t`Budúci mesiac`,
  nextYear: i18n.t`Budúci rok`,
  custom: i18n.t`Dátum`,
  all: i18n.t`Všetko`,
}

export interface DateIntervalFilterProps {
  onDateChange?: (args?: { from: Date; to: Date; dateType: string }) => void
  defaultValue?: DateIntervals
  dateFrom?: Date
  dateTo?: Date
  dateType?: DateIntervals
  resetToDefault?: boolean
  labelAllItems?: string
}

export const createDateInterval = (arg: string, custom?: { from: Date; to: Date }) => {
  let from: Date
  let to: Date

  switch (arg) {
    case DateIntervals.previousMonth:
      from = subMonths(addHours(endOfMonth(new Date()), 2), 2)
      to = subMonths(endOfMonth(new Date()), 1)
      break

    case DateIntervals.previousYear:
      from = subYears(
        addMilliseconds(addSeconds(addMinutes(startOfYear(startOfDay(new Date())), 59), 59), 999),
        1
      )
      to = subYears(addHours(endOfYear(new Date()), 1), 1)
      break

    case DateIntervals.thisMonth:
      from = addMilliseconds(
        addSeconds(addMinutes(addHours(startOfDay(startOfMonth(new Date())), 1), 59), 59),
        999
      )
      to = addHours(endOfMonth(new Date()), 2)
      break

    case DateIntervals.thisYear:
      from = addMilliseconds(
        addSeconds(addMinutes(startOfYear(startOfDay(new Date())), 59), 59),
        999
      )
      to = addHours(endOfYear(endOfDay(new Date())), 1)
      break

    case DateIntervals.nextMonth:
      from = addHours(endOfMonth(new Date()), 2)
      to = addHours(endOfMonth(addMonths(new Date(), 1)), 1)
      break

    case DateIntervals.nextYear:
      from = addHours(endOfYear(endOfMonth(new Date())), 1)
      to = addHours(addYears(endOfYear(new Date()), 1), 1)
      break

    case DateIntervals.custom:
      // eslint-disable-next-line prefer-destructuring
      from = startOfDay(custom!.from)
      // eslint-disable-next-line prefer-destructuring
      to = endOfDay(custom!.to)
      break
    default:
      return ''
  }

  return [from, to]
}

export const DateIntervalFilter = observer((props: DateIntervalFilterProps) => {
  const { defaultValue = 'all', onDateChange = () => ({}) } = props
  const [dateInterval, setDateInterval] = useState(DateIntervals[defaultValue])
  const [flatpickrValue, setFlatpickrValue] = useState<any>()
  const toggleRef = useRef<any>(null)

  const [state] = useState(() =>
    observable({
      title:
        defaultValue !== 'all'
          ? DateIntervalsLabelMap[props.defaultValue!]
          : props.labelAllItems ?? i18n.t`Dátum`,
    })
  )

  useEffect(() => {
    if (props.dateFrom && props.dateTo && props.defaultValue === DateIntervals.custom) {
      state.title = `${formatDate(props.dateFrom)} - ${formatDate(props.dateTo)}`
      handleDateChange(DateIntervals.custom, {
        from: props.dateFrom,
        to: props.dateTo,
      })
      setFlatpickrValue(
        createDateInterval(DateIntervals.custom, {
          from: new Date(props.dateFrom),
          to: new Date(props.dateTo),
        })
      )
    }
  }, [props.dateFrom, props.dateTo])

  useDidUpdate(() => {
    if (!props.dateFrom && !props.dateTo) {
      setDateInterval(DateIntervals.all)
      state.title = props.labelAllItems ? props.labelAllItems : i18n.t`Dátum`
    } else {
      setDateInterval(DateIntervals[props.dateType!])
    }
  }, [props.resetToDefault, props.dateFrom, props.dateTo])

  const handleDateChange = (arg: string, custom?: { from: Date; to: Date }) => {
    // close dropdown by simulating button click
    toggleRef?.current?.trigger?.click()

    const value = createDateInterval(arg, custom)
    setDateInterval(arg as DateIntervals)

    onDateChange(
      typeof value === 'string' ? undefined : { from: value[0], to: value[1], dateType: arg }
    )
  }

  return (
    <ButtonDropdown
      type="link"
      size="small"
      space="condensed"
      content={state.title}
      icon
      iconSize="medium"
      data-testid="date-interval-filter"
      dropdownInteractive
      getDropdownStaticRef={ref => {
        toggleRef.current = ref
      }}>
      <div
        className="detapicker--with-icon"
        css={css`
          &::before {
            top: 10px;
            right: 7px;
          }
          input {
            padding-right: 36px;
          }
        `}>
        <DatePicker
          onChange={e => {
            if (e.length === 2) {
              const from = e[0]
              const to = e[1]
              state.title = `${formatDate(from)} - ${formatDate(to)}`
              handleDateChange(DateIntervals.custom, {
                from,
                to,
              })
            }
          }}
          value={flatpickrValue}
          className="mb-none"
          options={{
            locale: Slovak,
            mode: 'range',
            dateFormat: 'Y-m-d',
            altFormat: 'd.m.Y',
            altInput: true,
          }}
        />
      </div>

      <DropdownItem
        className={cx(dateInterval === DateIntervals.thisMonth && 'is-highlighted')}
        onClick={() => {
          state.title = i18n.t`Tento mesiac`
          handleDateChange(DateIntervals.thisMonth)
        }}>
        <Trans>Tento mesiac</Trans>
      </DropdownItem>

      <DropdownItem
        data-testid="previous-month"
        className={cx(dateInterval === DateIntervals.previousMonth && 'is-highlighted')}
        onClick={() => {
          state.title = i18n.t`Minulý mesiac`
          handleDateChange(DateIntervals.previousMonth)
        }}>
        <Trans>Minulý mesiac</Trans>
      </DropdownItem>
      <DropdownItem
        className={cx(dateInterval === DateIntervals.nextMonth && 'is-highlighted')}
        onClick={() => {
          state.title = i18n.t`Budúci mesiac`
          handleDateChange(DateIntervals.nextMonth)
        }}>
        <Trans>Budúci mesiac</Trans>
      </DropdownItem>
      <DropdownItem
        className={cx(dateInterval === DateIntervals.thisYear && 'is-highlighted')}
        onClick={() => {
          state.title = i18n.t`Tento rok`
          handleDateChange(DateIntervals.thisYear)
        }}>
        <Trans>Tento rok</Trans>
      </DropdownItem>
      <DropdownItem
        className={cx(dateInterval === DateIntervals.previousYear && 'is-highlighted')}
        onClick={() => {
          state.title = i18n.t`Minulý rok`
          handleDateChange(DateIntervals.previousYear)
        }}>
        <Trans>Minulý rok</Trans>
      </DropdownItem>
      <DropdownItem
        className={cx(dateInterval === DateIntervals.nextYear && 'is-highlighted')}
        onClick={() => {
          state.title = i18n.t`Budúci rok`
          handleDateChange(DateIntervals.nextYear)
        }}>
        <Trans>Budúci rok</Trans>
      </DropdownItem>
      <DropdownItem
        className={cx(dateInterval === DateIntervals.all && 'is-highlighted')}
        onClick={() => {
          state.title = i18n.t`Všetko`
          handleDateChange(DateIntervals.all)
        }}>
        <Trans>Všetko</Trans>
      </DropdownItem>
    </ButtonDropdown>
  )
})

/**
 * Returns function used to filter `data` based on `from` to `to` date range of selected object's property (`prop`)
 */
export const createDateIntervalFilter = (from: Date, to: Date, prop: string) => (data: any[]) => {
  return data.filter(i => {
    const date = parse(i[prop])

    if (!isValid(date)) {
      // eslint-disable-next-line no-console
      console.warn(`Invalid date received: ${date}`)
      return false
    }
    return isWithinRange(date, from, to)
  })
}
